import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApisService } from '@core/services/apis.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-add-payments',
  templateUrl: './add-payments.component.html',
  styleUrls: ['./add-payments.component.scss'],
  standalone: false
})
export class AddPaymentsComponent implements OnInit {

  paymentData = {
    payment_method: '',
    account_number: '',
          paid_amount: null as number | null,
  payment_date: '' as string,
    transfer_file: null as File | null
  };

  selectedFile: File | null = null;
  imagePreview: string | null = null;
  fileName: string = '';
  fileSize: string = '';
  paymentId: string | null = null;
  isLoading: boolean = false;
  fileError: string = '';
  formError: string = '';
  currentLang: string = 'ar';

  // Validation constants
readonly allowedFileTypes = [
  'image/jpeg',
  'image/png',
  'image/jpg',
  'image/gif',
  'image/webp',
  'application/pdf'
];
  readonly maxFileSize = 5 * 1024 * 1024; // 5MB
  readonly minAccountNumberLength = 5;
  readonly maxAccountNumberLength = 50;

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private router: Router,
    private apisService: ApisService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.currentLang = this.translate.currentLang || 'ar';
    
    // الاشتراك في تغييرات اللغة
    this.translate.onLangChange.subscribe(event => {
      this.currentLang = event.lang;
    });

    this.paymentId = this.route.snapshot.paramMap.get('id');
    console.log('Payment ID from route:', this.paymentId);
    
    // تعيين قيمة افتراضية لطريقة الدفع بناءً على اللغة
    this.setDefaultPaymentMethod();
  }

  private setDefaultPaymentMethod(): void {
    if (this.currentLang === 'ar') {
      this.paymentData.payment_method = 'حساب بنكي';
    } else {
      this.paymentData.payment_method = 'Bank Account';
    }
  }

  // Trigger file input click
  triggerFileInput(): void {
    const fileInput = document.getElementById('transfer_file') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  // Handle drag over
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const uploadArea = event.target as HTMLElement;
    uploadArea.style.borderColor = '#cca44c';
    uploadArea.style.backgroundColor = '#f9f5eb';
  }

  // Handle drag leave
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const uploadArea = event.target as HTMLElement;
    uploadArea.style.borderColor = '#ccc';
    uploadArea.style.backgroundColor = '#f8f9fa';
  }

  // Handle drop
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    
    const uploadArea = event.target as HTMLElement;
    uploadArea.style.borderColor = '#ccc';
    uploadArea.style.backgroundColor = '#f8f9fa';

    if (event.dataTransfer?.files.length) {
      const fileInput = document.getElementById('transfer_file') as HTMLInputElement;
      if (fileInput) {
        fileInput.files = event.dataTransfer.files;
        this.onFileSelected({ target: { files: event.dataTransfer.files } });
      }
    }
  }

  // Remove selected image
  removeImage(): void {
    this.selectedFile = null;
    this.paymentData.transfer_file = null;
    this.imagePreview = null;
    this.fileName = '';
    this.fileSize = '';
    this.fileError = '';

    const fileInput = document.getElementById('transfer_file') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  onFileSelected(event: any): void {
    this.fileError = '';
    const file = event.target.files[0];
    
    if (file) {
      // التحقق من نوع الملف
      if (!this.allowedFileTypes.includes(file.type)) {
        this.fileError = this.translate.instant('MENU.DASHBOARD.file_type_not_supported');
        this.clearFileSelection();
        return;
      }

      // التحقق من حجم الملف
      if (file.size > this.maxFileSize) {
        this.fileError = this.translate.instant('MENU.DASHBOARD.file_size_exceeded');
        this.clearFileSelection();
        return;
      }

      this.selectedFile = file;
      this.paymentData.transfer_file = file;
      this.fileName = file.name;
      this.fileSize = this.formatFileSize(file.size);

      // معاينة الصورة
      // const reader = new FileReader();
      // reader.onload = (e: any) => {
      //   this.imagePreview = e.target.result;
      // };
      // reader.readAsDataURL(file);



      // Preview (Image only – PDF has no image preview)
if (file.type.startsWith('image/')) {
  const reader = new FileReader();
  reader.onload = (e: any) => {
    this.imagePreview = e.target.result;
  };
  reader.readAsDataURL(file);
} else {
  this.imagePreview = null; // PDF
}

    }
  }

  private clearFileSelection(): void {
    this.selectedFile = null;
    this.paymentData.transfer_file = null;
    this.imagePreview = null;
    this.fileName = '';
    this.fileSize = '';

    const fileInput = document.getElementById('transfer_file') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = this.currentLang === 'ar' 
      ? ['بايت', 'كيلوبايت', 'ميجابايت', 'جيجابايت']
      : ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // الفاليديشن المخصص
  private validateForm(): boolean {
    this.formError = '';
    this.fileError = '';

    // التحقق من الحقول الأساسية
    if (!this.paymentData.payment_method?.trim()) {
      this.formError = this.translate.instant('MENU.DASHBOARD.payment_method_required');
      return false;
    }

    if (this.paymentData.payment_method.trim().length < 2) {
      this.formError = this.translate.instant('MENU.DASHBOARD.payment_method_min_length');
      return false;
    }

    // التحقق من التاريخ
if (!this.paymentData.payment_date) {
  this.formError = this.translate.instant('MENU.DASHBOARD.payment_date_required');
  return false;
}


    // if (!this.paymentData.account_number?.trim()) {
    //   this.formError = this.translate.instant('MENU.DASHBOARD.account_number_required');
    //   return false;
    // }

        // التحقق من رقم الحساب فقط إذا تم إدخاله
if (this.paymentData.account_number && this.paymentData.account_number.trim().length < this.minAccountNumberLength) {
  this.formError = this.translate.instant('MENU.DASHBOARD.account_number_min_length');
  return false;
}
// التحقق من أن رقم الحساب أرقام فقط (إذا تم إدخاله)
const accountNumberRegex = /^[0-9]+$/;
if (this.paymentData.account_number && !accountNumberRegex.test(this.paymentData.account_number.trim())) {
  this.formError = this.translate.instant('MENU.DASHBOARD.account_number_pattern');
  return false;
}

    // if (this.paymentData.account_number.trim().length < this.minAccountNumberLength) {
    //   this.formError = this.translate.instant('MENU.DASHBOARD.account_number_min_length');
    //   return false;
    // }

    // التحقق من أن رقم الحساب يحتوي على أرقام فقط
    // const accountNumberRegex = /^[0-9]+$/;
    // if (!accountNumberRegex.test(this.paymentData.account_number.trim())) {
    //   this.formError = this.translate.instant('MENU.DASHBOARD.account_number_pattern');
    //   return false;
    // }

    // التحقق من الملف
    // if (!this.selectedFile) {
    //   this.formError = this.translate.instant('MENU.DASHBOARD.transfer_image_required');
    //   return false;
    // }

    return true;
  }

  onSubmit(): void {
    // إعادة تعيين الأخطاء
    this.formError = '';
    this.fileError = '';

    // الفاليديشن
    if (!this.validateForm()) {
      this.toastr.error(this.formError);
      return;
    }

    // التحقق من وجود paymentId
    if (!this.paymentId) {
      const errorMsg = this.currentLang === 'ar' 
        ? 'معرف الدفعة مفقود' 
        : 'Payment ID is missing';
      this.toastr.error(errorMsg);
      return;
    }

    this.isLoading = true;

    // إنشاء FormData
    const formData = new FormData();
    formData.append('payment_method', this.paymentData.payment_method.trim());
formData.append('account_number', this.paymentData.account_number ? this.paymentData.account_number.trim() : '');        formData.append('paid_amount', String(this.paymentData.paid_amount));
formData.append('payment_date', this.paymentData.payment_date);
    
    if (this.selectedFile) {
      formData.append('transfer_file', this.selectedFile, this.selectedFile.name);
    }

    // إرسال البيانات
    this.apisService.addPayments(this.paymentId, formData).subscribe({
      next: (response: any) => {
        console.log('✅ Payment added successfully:', response);
        this.toastr.success(this.translate.instant('MENU.DASHBOARD.payment_added_successfully'));
        this.resetForm();
        this.isLoading = false;
        
        // العودة للقائمة بعد النجاح
        setTimeout(() => {
          this.router.navigate(['/analytics']);
        }, 2000);
      },
      error: (error) => {
        console.error('🔴 Error details:', error);
        this.handleError(error);
        this.isLoading = false;
      }
    });
  }

  private handleError(error: any): void {
    if (error.status === 422 && error.error?.errors) {
      const errors = error.error.errors;
      let errorMessage = '';
      
      for (const field in errors) {
        if (errors.hasOwnProperty(field)) {
          errorMessage += `${this.translateFieldName(field)}: ${errors[field].join(', ')}\n`;
        }
      }
      this.toastr.error(errorMessage);
    } else {
      this.toastr.error(this.translate.instant('MENU.DASHBOARD.error_adding_payment'));
    }
  }

  private translateFieldName(field: string): string {
    const fieldNames: { [key: string]: { ar: string, en: string } } = {
      'payment_method': { ar: 'طريقة الدفع', en: 'Payment Method' },
      'account_number': { ar: 'رقم الحساب', en: 'Account Number' },
      'transfer_file': { ar: 'صورة التحويل', en: 'Transfer Image' }
    };
    
    const fieldName = fieldNames[field];
    return fieldName ? fieldName[this.currentLang as 'ar' | 'en'] : field;
  }

  resetForm(): void {
    this.paymentData = {
      payment_method: this.currentLang === 'ar' ? 'حساب بنكي' : 'Bank Account',
      account_number: '',
        payment_date: '',
        paid_amount: null,
      transfer_file: null
    };
    this.selectedFile = null;
    this.imagePreview = null;
    this.fileName = '';
    this.fileSize = '';
    this.fileError = '';
    this.formError = '';

    const fileInput = document.getElementById('transfer_file') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  // دالة للمساعدة في الحصول على نص الترجمة
  getTranslation(key: string): string {
    return this.translate.instant(key);
  }
}